import React from 'react';
import Router from 'react-router/lib/Router';
import IndexRoute from 'react-router/lib/IndexRoute';
import Route from 'react-router/lib/Route';
import { hashHistory } from 'react-router';
import Store from './store';
// Components/modules
import App from './App';
import Dashboard from './modules/dashboard';
import Login from './modules/login';
import { Run } from './modules/runs';
import { Manifest, Manifests } from './modules/manifests';
import RFU from './modules/reports/rfu/containers/RFU';
import AbnormalityKPI from './modules/reports/abnormalityKPI/AbnormalityKPI';
import PlanningKPI from './modules/reports/planningKPI/PlanningKPI';
import IlocsDataDownload from './modules/reports/ilocs/IlocsDataDownload';
import AbnormalityAnalysis from './modules/recoveryActions/analysis/containers/AbnormalityAnalysis';
import RunsList from './components/RunsList/RunsList';
import GenericAbnormalityAnalysis from './modules/recoveryActions/analysis/containers/GenericAbnormalityAnalysis';
import JourneyBuilder from './modules/recoveryActions/journeybuilder/containers/JourneyBuilder';
import AbnormalityTypesRouteHandler from './components/AbnormalityTypesRouteHandler';
import RecoveryActionTypesRouteHandler from './components/RecoveryActionTypesRouteHandler';
// Utils
import requireAuthAndAuthz from './shared/utils/requireAuthAndAuthz';
import canEnterWhenAuthenticated from './shared/utils/canEnterWhenAuthenticated';
import noMatchingRoute from './shared/utils/noMatchingRoute';
import {
    canViewAbnormalityDetails,
    canViewManifestDetails,
    canViewRecoveryActionDetails,
    canViewRunDetails,
} from './shared/security/isActionAuthorized';
import { showAccessDeniedToast } from './shared/security/showAccessDeniedToast';
import { getSelfFromResourceLinks } from './shared/utils/link';

const authenticateAndAuthorize = requireAuthAndAuthz(Store.store);
const redirectWhenLoggedIn = canEnterWhenAuthenticated(Store.store);
const noRoute = noMatchingRoute(Store.store);
const authorize = authzFunction => (nextState, replace, callback) => {
    if (authzFunction()) {
        callback();
    } else {
        showAccessDeniedToast();
    }
};

const handleRunClick = run => {
    const link = getSelfFromResourceLinks(run);
    hashHistory.push(link);
};

export default (
    <Router history={Store.history} onUpdate={() => window.scrollTo(0, 0)}>
        <Route path="/" name="Home" component={App}>
            <IndexRoute component={Dashboard} onEnter={authenticateAndAuthorize} />
            <Route path="dashboard" name="Dashboard" onEnter={authenticateAndAuthorize}>
                <IndexRoute component={Dashboard} />
            </Route>
            <Route path="runs" name="Runs" onEnter={authenticateAndAuthorize}>
                <IndexRoute component={RunsList} onRunClick={handleRunClick} />
                <Route path=":id" name="Run" prettifyParam={param => `Run (${param})`}>
                    <Route
                        path="abnormality/:type"
                        name="Create abnormality"
                        manifestBased={false}
                        prettifyParam={param => `Create ${param}`}
                        component={AbnormalityTypesRouteHandler}
                    />
                    <Route
                        path="abnormality/:type/:abnormalityId"
                        name="Abnormality details"
                        prettifyParam={param => `Abnormality (${param})`}
                        onEnter={({ params: { type } }, replace, callback) =>
                            canViewAbnormalityDetails(type) ? callback() : showAccessDeniedToast()
                        }
                    >
                        <Route path="genericAbnormalityAnalysis" name="Abnormality analysis">
                            <Route
                                name="Journey Builder"
                                path="journeyBuilder"
                                component={JourneyBuilder}
                            />
                            <IndexRoute
                                component={GenericAbnormalityAnalysis}
                                prettifyParam={param => `Abnormality Analysis (${param})`}
                            />
                        </Route>
                        <Route
                            path="recoveryAction/create"
                            name="Create recovery action"
                            component={AbnormalityAnalysis}
                        />
                        <Route
                            path="recoveryAction/:recType/:recoveryActionId"
                            name="Recovery action details"
                            prettifyParam={param => `Recovery action (${param})`}
                            component={RecoveryActionTypesRouteHandler}
                            onEnter={({ params: { recType } }, replace, callback) =>
                                canViewRecoveryActionDetails(recType)
                                    ? callback()
                                    : showAccessDeniedToast()
                            }
                        />
                        <IndexRoute
                            component={AbnormalityTypesRouteHandler}
                            manifestBased={false}
                        />
                    </Route>
                    <IndexRoute component={Run} onEnter={authorize(canViewRunDetails)} />
                </Route>
            </Route>
            <Route path="manifests" name="Manifests" onEnter={authenticateAndAuthorize}>
                <IndexRoute component={Manifests} onEnter={authorize(canViewManifestDetails)} />
                <Route path=":id" name="Manifest" prettifyParam={param => `Manifest (${param})`}>
                    <IndexRoute component={Manifest} />
                    <Route
                        path="abnormality/:type"
                        name="Create abnormality"
                        manifestBased
                        component={AbnormalityTypesRouteHandler}
                    />
                    <Route
                        path="abnormality/:type/:abnormalityId"
                        name="Abnormality details"
                        prettifyParam={param => `Abnormality (${param})`}
                        onEnter={({ params: { type } }, replace, callback) =>
                            canViewAbnormalityDetails(type) ? callback() : showAccessDeniedToast()
                        }
                    >
                        <Route path="genericAbnormalityAnalysis" name="Abnormality analysis">
                            <Route
                                name="Journey Builder"
                                path="journeyBuilder"
                                component={JourneyBuilder}
                            />
                            <IndexRoute
                                component={GenericAbnormalityAnalysis}
                                prettifyParam={param => `Abnormality Analysis (${param})`}
                            />
                        </Route>
                        <Route
                            path="recoveryAction/:recType/:recoveryActionId"
                            name="Recovery action details"
                            prettifyParam={param => `Recovery action (${param})`}
                            component={RecoveryActionTypesRouteHandler}
                            onEnter={({ params: { recType } }, replace, callback) =>
                                canViewRecoveryActionDetails(recType)
                                    ? callback()
                                    : showAccessDeniedToast()
                            }
                        />
                        <IndexRoute component={AbnormalityTypesRouteHandler} manifestBased />
                    </Route>
                </Route>
            </Route>
            <Route path="reports" name="Reports" onEnter={authenticateAndAuthorize}>
                <Route path="kpi" name="Abnormality KPI report" component={AbnormalityKPI} />
                <Route path="planningKpi" name="Planning KPI report" component={PlanningKPI} />
                <Route path="rfu" name="RFU" component={RFU} />
                <Route path="ilocsDataDownload" name="ILOCSDataDownload" component={IlocsDataDownload} />
            </Route>
            <Route path="login" name="Log in" component={Login} onEnter={redirectWhenLoggedIn} />
        </Route>
        <Route path="*" name="Not found..." onEnter={noRoute} />
    </Router>
);
